import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag
} from 'antd';
import { ClearOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import ModalAdd from './ModalAdd';
import {
  apiCreateMasterData,
  apiDeleteMasterData,
  apiGetMasterData,
  apiUpdateMasterData
} from '@services/MasterData/MasterDataService';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Option } = Select;

const TYPE_LABEL = {
  CN: 'Chi nhánh',
  TQL: 'Tổ quản lý',
  DHK: 'Đồng hồ khối',
  TUYEN: 'Tuyến',
  METER_NAME: 'Tên đồng hồ',
  METER_SIZE: 'Cỡ đồng hồ'
};

// These types have a Chi nhánh parent filter
const USES_CN_FILTER = ['TQL', 'DHK'];
// These types have a Tổ quản lý parent filter (cascaded under Chi nhánh)
const USES_TQL_FILTER = ['DHK'];

/**
 * Component mounts fresh on every tab switch (key={activeType} in parent).
 * No internal tab-switch cleanup is needed.
 */
const List = React.forwardRef(function List({ activeType = 'CN', onCountChange }, ref) {
  const { masterData } = usePermission();

  // ── Table data
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // ── Filters
  const [search, setSearch] = useState('');
  const [cnFilter, setCnFilter] = useState(undefined);
  const [tqlFilter, setTqlFilter] = useState(undefined);

  // ── Dropdown options
  const [cnOptions, setCnOptions] = useState([]);
  const [tqlFilterOpts, setTqlFilterOpts] = useState([]);

  // ── Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // ── Refs to hold latest values without making fetchData a dep of effects
  const searchRef = useRef(search);
  const cnFilterRef = useRef(cnFilter);
  const tqlFilterRef = useRef(tqlFilter);
  const pageSizeRef = useRef(pageSize);
  searchRef.current = search;
  cnFilterRef.current = cnFilter;
  tqlFilterRef.current = tqlFilter;
  pageSizeRef.current = pageSize;

  // ── Core fetch ────────────────────────────────────────────────────────────
  // Callers always pass explicit overrides; refs are only fallback.
  const fetchData = async (overrides = {}) => {
    setLoading(true);
    try {
      const cn = 'cn' in overrides ? overrides.cn : cnFilterRef.current;
      const tql = 'tql' in overrides ? overrides.tql : tqlFilterRef.current;
      const kw = 'keyword' in overrides ? overrides.keyword : searchRef.current;
      const pg = overrides.page ?? 1;
      const sz = overrides.size ?? pageSizeRef.current;

      const params = {
        page: pg,
        size: sz,
        keyWord: kw?.trim() || undefined,
        type: activeType
      };

      if (activeType === 'TQL') {
        // parent_level_1_id = Chi nhánh value
        params.parent_level_1_id = cn || undefined;
      } else if (activeType === 'DHK') {
        // parent_level_2_id = Chi nhánh, parent_level_1_id = Tổ quản lý
        params.parent_level_2_id = cn || undefined;
        params.parent_level_1_id = tql || undefined;
      }

      const res = await apiGetMasterData(params);
      const data = res.data?.data ?? res.data ?? [];
      const tot = res.data?.meta?.total ?? (Array.isArray(data) ? data.length : 0);

      setList(Array.isArray(data) ? data : []);
      setPage(pg);
      setPageSize(sz);
      setTotal(tot);
      onCountChange?.(activeType, tot);
    } catch {
      message.error('Lấy dữ liệu thất bại');
    } finally {
      setLoading(false);
    }
  };

  // Initial load on mount
  useEffect(() => {
    fetchData({ page: 1 });
  }, []);

  // ── Load Chi nhánh options (filter row + modal)
  useEffect(() => {
    if (!USES_CN_FILTER.includes(activeType)) return;
    apiGetMasterData({ type: 'CN', page: 1, size: 1000 })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setCnOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, [activeType]);

  // ── Load Tổ quản lý options for DHK filter (cascaded by cnFilter)
  useEffect(() => {
    if (!USES_TQL_FILTER.includes(activeType)) return;
    const params = { type: 'TQL', page: 1, size: 1000 };
    if (cnFilter) params.parent_level_1_id = cnFilter;
    apiGetMasterData(params)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setTqlFilterOpts(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, [activeType, cnFilter]);

  // ── Event handlers ────────────────────────────────────────────────────────
  const handleSearch = () => fetchData({ page: 1 });

  const handleCnFilterChange = (val) => {
    setCnFilter(val);
    setTqlFilter(undefined);
    fetchData({ page: 1, cn: val, tql: undefined });
  };

  const handleTqlFilterChange = (val) => {
    setTqlFilter(val);
    fetchData({ page: 1, tql: val });
  };

  const handleClearFilter = () => {
    setSearch('');
    setCnFilter(undefined);
    setTqlFilter(undefined);
    fetchData({ page: 1, keyword: '', cn: undefined, tql: undefined });
  };

  const handleTableChange = ({ current, pageSize: sz }) => {
    fetchData({ page: current, size: sz });
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  useImperativeHandle(ref, () => ({ openCreate }));

  const handleSubmit = async (values) => {
    try {
      if (editing?.id) {
        await apiUpdateMasterData(editing.id, values);
        message.success('Cập nhật thành công');
      } else {
        await apiCreateMasterData(values);
        message.success('Thêm mới thành công');
      }
      closeModal();
      fetchData({ page: 1 });
    } catch {
      message.error('Thao tác thất bại');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteMasterData(id);
      message.success('Xoá thành công');
      fetchData({ page: 1 });
    } catch {
      message.error('Xoá thất bại');
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const cols = [
      { title: 'Mã', dataIndex: 'value', width: 50 },
      { title: 'Tên hiển thị', dataIndex: 'name', width: 200 }
    ];

    if (activeType === 'TQL') {
      cols.push({ title: 'Thuộc Chi nhánh', dataIndex: 'parent_level_1_name', width: 200 });
    }

    if (activeType === 'DHK') {
      cols.push({ title: 'Thuộc Chi nhánh', dataIndex: 'parent_level_2_name', width: 200 });
      cols.push({ title: 'Thuộc Tổ quản lý', dataIndex: 'parent_level_1_name', width: 200 });
    }

    cols.push(
      {
        title: 'Trạng thái',
        dataIndex: 'is_active',
        width: 120,
        render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Hoạt động' : 'Ngừng'}</Tag>
      },
      {
        title: 'Tạo bởi',
        dataIndex: 'created_by',
        width: 140,
        render: (v) => v || '—'
      },
      {
        title: 'Thời gian tạo',
        dataIndex: 'created_at',
        width: 160,
        render: (v) => (v ? new Date(v).toLocaleString('vi-VN', { hour12: false }) : '—')
      },
      {
        title: 'Thời gian cập nhật',
        dataIndex: 'updated_at',
        width: 160,
        render: (v) => (v ? new Date(v).toLocaleString('vi-VN', { hour12: false }) : '—')
      },
      {
        title: 'Thao tác',
        dataIndex: 'actions',
        width: 80,
        fixed: 'right',
        render: (_, record) => (
          <Space size={4}>
            {masterData.edit && (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            )}
            {masterData.delete && (
              <Popconfirm
                title={`Xác nhận xoá "${record.name}"?`}
                okText="Xoá"
                cancelText="Huỷ"
                onConfirm={() => handleDelete(record.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        )
      }
    );

    return cols;
  }, [activeType, masterData]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Filter card ── */}
      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]} align="middle">
          {/* Chi nhánh filter */}
          {USES_CN_FILTER.includes(activeType) && (
            <Col xs={24} sm={12} md={5} lg={5}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder="Chọn Chi nhánh"
                style={{ width: '100%', height: 36 }}
                value={cnFilter}
                onChange={handleCnFilterChange}>
                {cnOptions.map((opt) => (
                  <Option key={opt.id} value={opt.value}>
                    {opt.name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Tổ quản lý filter (DHK only) */}
          {USES_TQL_FILTER.includes(activeType) && (
            <Col xs={24} sm={12} md={5} lg={5}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder="Chọn Tổ quản lý"
                style={{ width: '100%', height: 36 }}
                value={tqlFilter}
                onChange={handleTqlFilterChange}>
                {tqlFilterOpts.map((opt) => (
                  <Option key={opt.id} value={opt.value}>
                    {opt.name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Search input */}
          <Col xs={24} sm={14} md={6} lg={6}>
            <Input
              style={{ height: 36, width: '100%' }}
              placeholder={`Tìm kiếm tên / mã ${TYPE_LABEL[activeType] ?? ''}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          {/* Buttons */}
          <Col flex="none">
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                style={{ height: 36, backgroundColor: '#0284c7', borderColor: '#0284c7' }}
                onClick={handleSearch}>
                Tìm kiếm
              </Button>
              <Button icon={<ClearOutlined />} style={{ height: 36 }} onClick={handleClearFilter}>
                Xoá lọc
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Table ── */}
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ marginTop: 8 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (tot, range) => `${range[0]}-${range[1]} của ${tot} bản ghi`
        }}
        onChange={handleTableChange}
      />

      {/* Modal tạo / chỉnh sửa */}
      <ModalAdd
        open={modalOpen}
        onCancel={closeModal}
        onSubmit={handleSubmit}
        type={activeType}
        cnOptions={cnOptions}
        initialValues={editing}
      />
    </div>
  );
});

export default List;
